import {
  Button,
  MultiSelectDropDown,
  SelectDropDown,
  TextField,
} from '@/components/Common';
import { Calendar } from '@/components/Common/Calendar/Calendar';
import moneyIcon from '@/assets/icons/money.svg';
import {
  IconPosition,
  TextfieldType,
} from '@/components/Common/TextField/TextField.types';
import { Modal } from '@/components/Layout';
import { FormMode, useCoupons } from '@/pages/Coupons/coupons.hook';
import React, { FC, useEffect } from 'react';
import { FieldError } from 'react-hook-form';
import dateIcon from '@/assets/icons/date.svg';

interface CouponFormProps {
  mode: FormMode;
}

const CouponForm: FC<CouponFormProps> = ({ mode }) => {
  const {
    isOpenCouponFormModal,
    openCouponFormModal,
    isUpdatePending,
    isCreatePending,
    handleSubmit,
    handleSubmitUpdate,
    register,
    registerUpdateForm,
    updateErrors,
    errors,
    setValue,
    setUpdateValue,
    createCouponHandler,
    updateCouponHandler,
    handleSelectedDiscountType,
    statusType,
    packages,
    discountType,
    coupon,
    formatDate,
  } = useCoupons();

  const isUpdateMode = mode === 'update';

  const onSubmit = isUpdateMode ? updateCouponHandler : createCouponHandler;
  const handleSubmitFn = isUpdateMode ? handleSubmitUpdate : handleSubmit;
  const registerFn = isUpdateMode ? registerUpdateForm : register;
  const errorList = isUpdateMode ? updateErrors : errors;

  useEffect(() => {
    if (isUpdateMode && coupon) {
      setUpdateValue('code', coupon.code);
      setUpdateValue('usageLimit', coupon.usageLimit);
      setUpdateValue('discountType', coupon.discountType);
      setUpdateValue('value', coupon.discountValue);
      setUpdateValue('expirationDate', coupon.expirationDate);
    }
  }, [coupon, isUpdateMode, setUpdateValue]);

  return (
    <Modal
      isOpen={isOpenCouponFormModal}
      onClose={openCouponFormModal}
      modalStyle="bg-[#2A2A2A] w-[25rem] px-7"
      headerStyle="bg-[#2A2A2A] mt-1"
    >
      <form
        className="flex flex-col gap-2 overflow-y-auto"
        onSubmit={handleSubmitFn(onSubmit)}
      >
        <h3 className="text-white text-xl font-bold text-center mt-0 mb-5">
          {isUpdateMode ? 'Modifier code promo' : '  Créer un code promo'}
        </h3>
        <SelectDropDown
          options={statusType || []}
          placeholder="Type de réduction"
          name="discountType"
          selectDropDownStyle={{
            borderRadius: '0.375rem',
            color: '#fff',
            padding: ' 0.5rem',
          }}
          setValue={isUpdateMode ? setUpdateValue : setValue}
          handleChange={handleSelectedDiscountType}
          initialSelectedOptions={
            isUpdateMode ? { _id: 1, name: coupon?.discountType } : null
          }
          helpText={(errorList.discountType as FieldError)?.message}
          errorStyle="text-red-500"
        />
        <MultiSelectDropDown
          options={packages || []}
          maxSelectedLabels={5}
          setValue={isUpdateMode ? setUpdateValue : setValue}
          initialSelectedOptions={
            isUpdateMode
              ? coupon?.packages.map((item: { _id: string; type: string }) => {
                  return { _id: item._id, name: item.type };
                }) || []
              : undefined
          }
          placeholder="Package"
          name="package"
          helpText={(errorList.package as FieldError)?.message}
          errorStyle="text-red-500"
          selectDropDownStyle={{
            borderRadius: '0.375rem',
            padding: ' 0.5rem',
          }}
        />

        <TextField
          textFieldType={TextfieldType.INPUT}
          placeholder="Code promo"
          inputStyle={{
            background: '#232323',
            fontSize: '13.8px',
          }}
          keyfilter="alphanum"
          name="code"
          helpText={(errorList.code as FieldError)?.message}
          register={registerFn('code')}
          errorStyle="text-red-500"
        />

        <TextField
          textFieldType={TextfieldType.INPUT}
          placeholder="Nombre d'usage"
          inputStyle={{ background: '#232323', fontSize: '13.8px' }}
          name="usageLimit"
          keyfilter="num"
          register={registerFn('usageLimit')}
          helpText={(errorList.usageLimit as FieldError)?.message}
          errorStyle="text-red-500"
        />
        <div className="bg-[#232323] rounded-[6px] max-h-12 pl-3">
          <TextField
            textFieldType={TextfieldType.INPUT}
            placeholder="valeur"
            keyfilter="num"
            inputStyle={{ background: '#232323' }}
            name="value"
            icon={
              discountType === 'FIXED' ? (
                <img src={moneyIcon} alt="money" className="mr-1" />
              ) : (
                <i className="pi pi-percentage text-[#939393] text-sm"></i>
              )
            }
            iconPosition={IconPosition.LEFT}
            register={registerFn('value')}
          />
        </div>
        <div className="text-red-500 text-xs">
          {(errorList.value as FieldError)?.message}
        </div>

        <Calendar
          name="expirationDate"
          label={
            isUpdateMode
              ? formatDate(coupon?.expirationDate)
              : "Date d'expiraton"
          }
          setValue={isUpdateMode ? setUpdateValue : setValue}
          helpText={errorList.expirationDate?.message}
          errorStyle="text-red-500 text-xs"
          minDate={new Date()}
          dateIcon={dateIcon}
          className=" bg-[#232323] text-[13.8px] text-white font-normal placeholder-[#939393]"
          mode={isUpdateMode}
        />

        <Button
          title={`${isUpdateMode ? (isUpdatePending ? 'Modification...' : 'Modifier') : isCreatePending ? 'Ajout en cours...' : 'Ajouter'}`}
          className="my-2 mx-1"
          type="submit"
          disabled={isUpdatePending || isCreatePending}
        />
      </form>
    </Modal>
  );
};

export default CouponForm;
